import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, ListChecks, FileText, ShieldCheck, GraduationCap,
  Settings, LogOut, Menu, UserCircle, Contact, Webhook, ScrollText, BookOpen, Globe, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";

const ADMIN_ROLES = ["super_admin", "admin"];

function navItems(t, role) {
  const items = [
    { to: "/portal", icon: LayoutDashboard, label: t("Dashboard", "Dashboard"), end: true },
  ];
  if (role !== "trainee") {
    items.push({ to: "/portal/leads", icon: Contact, label: "Leads" });
  }
  items.push(
    { to: "/portal/aufgaben", icon: ListChecks, label: t("Aufgaben", "Tasks") },
    { to: "/academy", icon: GraduationCap, label: "Eleven Academy" },
    { to: "/portal/dokumente", icon: FileText, label: t("Dokumente", "Documents") },
    { to: "/portal/compliance", icon: ShieldCheck, label: "Compliance Center" },
  );
  return items;
}

function adminItems(t) {
  return [
    { to: "/admin", icon: LayoutDashboard, label: t("\u00dcbersicht", "Overview"), end: true },
    { to: "/admin/benutzer", icon: Users, label: t("Benutzer", "Users") },
    { to: "/admin/kurse", icon: BookOpen, label: t("Kurse", "Courses") },
    { to: "/admin/dokumente", icon: FileText, label: t("Dokumente & Vorlagen", "Documents & Templates") },
    { to: "/admin/inhalte", icon: Globe, label: t("Website-Inhalte", "Website Content") },
    { to: "/admin/formulare", icon: ListChecks, label: t("Formulare", "Forms") },
    { to: "/admin/postausgang", icon: Mail, label: t("Postausgang", "Outbox") },
    { to: "/admin/crm", icon: Webhook, label: "CRM / GHL" },
    { to: "/admin/audit", icon: ScrollText, label: "Audit-Logs" },
    { to: "/admin/einstellungen", icon: Settings, label: t("Einstellungen", "Settings") },
  ];
}

function SidebarContent({ onNavigate }) {
  const { t, tt } = useLang();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isCompliance = user?.role === "compliance";

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm font-medium transition-colors ${
      isActive ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"
    }`;

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5">
        <Link to="/" className="flex items-center" data-testid="shell-logo">
          <img src="/brand/eleven-logo-light-sm.png" alt="Eleven Family Office" className="h-[22px] w-auto shrink-0" />
        </Link>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {navItems(t, user?.role).map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={linkCls} onClick={onNavigate} data-testid={`sidebar-${item.to.replace(/\//g, "-").slice(1)}`}>
            <item.icon className="h-4 w-4" /> {item.label}
          </NavLink>
        ))}
        {(isAdmin || isCompliance) && (
          <>
            <p className="px-3 pt-5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white/30">
              {t("Eleven Admin Center", "Eleven Admin Center")}
            </p>
            {isAdmin
              ? adminItems(t).map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end} className={linkCls} onClick={onNavigate} data-testid={`sidebar-${item.to.replace(/\//g, "-").slice(1)}`}>
                    <item.icon className="h-4 w-4" /> {item.label}
                  </NavLink>
                ))
              : (
                <NavLink to="/admin/audit" className={linkCls} onClick={onNavigate} data-testid="sidebar-admin-audit">
                  <ScrollText className="h-4 w-4" /> Audit-Logs
                </NavLink>
              )}
          </>
        )}
      </nav>
      <div className="p-4">
        <div className="rounded-[12px] bg-white/5 border border-white/10 p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#D6C08D]/20 flex items-center justify-center text-[#D6C08D] text-sm font-semibold">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate" data-testid="shell-user-name">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-white/45">{tt(ROLE_LABELS[user?.role])}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t, lang, setLang, tt } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let area = "Eleven Mitarbeiterportal";
    if (pathname.startsWith("/academy")) area = "Eleven Academy";
    else if (pathname.startsWith("/admin")) area = "Eleven Admin Center";
    else if (pathname.startsWith("/portal/compliance")) area = "Eleven Compliance Center";
    document.title = `${area} | Eleven Family Office`;
    // Interne Bereiche niemals indexieren lassen
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow, noarchive");
    return () => {
      // Beim Verlassen interner Bereiche wieder indexierbar (oeffentliche Seiten)
      const r = document.querySelector('meta[name="robots"]');
      if (r) r.setAttribute("content", "index, follow");
    };
  }, [pathname]);

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-shell">
      <aside className="hidden lg:flex flex-col w-[264px] shrink-0 bg-[#0B1220] fixed inset-y-0">
        <SidebarContent />
      </aside>
      <div className="flex-1 lg:ml-[264px] flex flex-col min-h-screen">
        <header className="h-14 sticky top-0 z-40 border-b border-border bg-white/85 backdrop-blur-md flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden rounded-[10px]" data-testid="shell-mobile-menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-[#0B1220] border-white/10">
                <SidebarContent onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("Willkommen,", "Welcome,")} <span className="font-medium text-foreground">{user?.first_name}</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 rounded-[10px]" data-testid="shell-language-switcher">
                  <Globe className="h-4 w-4" /> <span className="uppercase text-xs font-semibold">{lang}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLang("de")}>Deutsch {lang === "de" && "\u2713"}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang("en")}>English {lang === "en" && "\u2713"}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 rounded-[10px]" data-testid="shell-user-menu">
                  <UserCircle className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm">{user?.first_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground">{tt(ROLE_LABELS[user?.role])}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/portal/profil")} data-testid="shell-profile-link">
                  <UserCircle className="h-4 w-4 mr-2" /> {t("Profil", "Profile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  data-testid="shell-logout-button"
                >
                  <LogOut className="h-4 w-4 mr-2" /> {t("Abmelden", "Sign out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
